import { useQuery } from "@tanstack/react-query";

async function fetchPdfBlob(url: string): Promise<Blob> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`Failed to fetch PDF: ${res.status}`);
  return res.blob();
}

/**
 * Fetches the PDF and caches it as a Blob in React Query.
 *
 * Blob (not ArrayBuffer) is used deliberately: PDF.js internally transfers
 * ArrayBuffers into its worker, detaching the original buffer so subsequent
 * renders throw "Cannot perform Construct on a detached ArrayBuffer".
 * Blobs are immutable and never detached — safe to pass to <Document> on
 * every render.
 *
 * staleTime: Infinity — binary content never goes stale while in memory.
 * gcTime: 30 min      — survives unmount so navigating back is instant.
 */
export function usePdfDocument(url: string) {
  return useQuery({
    queryKey: ["pdf-document", url],
    queryFn: () => fetchPdfBlob(url),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: 2,
  });
}
